
import { db, storage, functions } from '../../lib/firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { Studio, StudioTeamMember, UserProfile } from '../../types';
import { updateUserProfile } from './users';

// --- STUDIO PROFILE ---

export const getStudioByOwner = async (ownerId: string): Promise<Studio | null> => {
    const snap = await db.collection('studios').where('ownerId', '==', ownerId).limit(1).get();
    if (!snap.empty) {
        const doc = snap.docs[0];
        const publicData = { id: doc.id, ...doc.data() } as Studio;
        
        // Try to fetch private config (if owner) to populate form
        try {
            // Note: This read will fail if not owner, which is fine
            const secretSnap = await db.collection('studios').doc(doc.id).collection('config').doc('secrets').get();
            if (secretSnap.exists) {
                // SECURITY: Do not return encrypted passwords to client to prevent double-encryption on save
                // We only return non-sensitive config (host/port/email) so they can be edited.
                // Passwords must be re-entered if changed.
                const { smtpPassword, whatsappApiKey, ...safeSecrets } = secretSnap.data() as any;
                return { ...publicData, ...safeSecrets };
            }
        } catch(e) {
            // Ignore permission errors, just return public data
        }
        
        return publicData;
    }
    return null;
};

export const createStudio = async (studio: Omit<Studio, 'id'>) => {
    return await db.collection('studios').add(studio);
};

export const updateStudio = async (id: string, data: Partial<Studio>) => {
    // Separate Secrets
    const { smtpPassword, whatsappApiKey, ...publicData } = data;
    
    // Update public profile
    await db.collection('studios').doc(id).update(publicData);
    
    // Save secrets securely via Cloud Function if present
    if (smtpPassword || whatsappApiKey || data.smtpHost || data.smtpEmail || data.whatsappApiUrl) {
        await saveStudioSecrets(id, {
            smtpPassword,
            whatsappApiKey,
            smtpHost: data.smtpHost,
            smtpPort: data.smtpPort,
            smtpEmail: data.smtpEmail,
            whatsappApiUrl: data.whatsappApiUrl
        });
    }
};

export const saveStudioSecrets = async (studioId: string, secrets: any) => {
    const saveFn = functions.httpsCallable('saveStudioSecrets');
    await saveFn({ studioId, ...secrets });
};

export const uploadStudioLogo = async (studioId: string, file: File): Promise<string> => {
    const path = `studios/${studioId}/logo_${Date.now()}_${file.name}`;
    const storageRef = ref(storage, path);
    await uploadBytes(storageRef, file);
    return getDownloadURL(storageRef);
};

export const uploadBrandAsset = async (studioId: string, file: File): Promise<string> => {
    // Use a specific folder for brand assets to keep things organized
    const path = `studios/${studioId}/brands/${Date.now()}_${file.name}`;
    const storageRef = ref(storage, path);
    await uploadBytes(storageRef, file);
    return getDownloadURL(storageRef);
};

// --- TEAM MANAGEMENT ---

export const getStudioTeam = async (studioId: string): Promise<StudioTeamMember[]> => {
    const snap = await db.collection('studio_team').where('studioId', '==', studioId).get();
    return snap.docs.map(d => ({ id: d.id, ...d.data() } as StudioTeamMember));
};

export const inviteTeamManager = async (studioId: string, email: string, name: string) => {
    // 1. Check limit
    const currentTeam = await getStudioTeam(studioId);
    if (currentTeam.filter(m => m.status === 'ACTIVE' || m.status === 'INVITED').length >= 3) {
        throw new Error("Team limit reached (Max 3).");
    }

    // 2. Check if user exists (Optional: if not, creates a shadow profile or sends email invite via function)
    // For now, we assume we create a record pending acceptance.
    
    await db.collection('studio_team').add({
        studioId,
        email,
        name,
        role: 'MANAGER',
        permissions: ['calendar', 'bookings', 'orders'],
        status: 'INVITED',
        invitedAt: Date.now()
    } as Omit<StudioTeamMember, 'id' | 'userId'>);
};

export const removeTeamMember = async (teamMemberId: string) => {
    await db.collection('studio_team').doc(teamMemberId).delete();
    // Ideally also revert the user's role if they are no longer managing anything, 
    // but complexity increases (one user might manage multiple studios in future).
};

export const suspendTeamMember = async (teamMemberId: string, suspend: boolean) => {
    await db.collection('studio_team').doc(teamMemberId).update({
        status: suspend ? 'SUSPENDED' : 'ACTIVE'
    });
};
